
// --- React ---
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// --- Services ---
import { useUnsavedChangesWarning } from "../../../hooks/useUnsavedChangesWarning";
import parameterUnitSvc from "../../../services/api/parameterUnit";

// --- Components ---
import ModalConfirm from "../../../components/modal-comfirm";
import { notify } from "../../../common/notificationService";
import { omitMetaFields } from "../../../utils/helper";
import ButtonGoback from "../../../components/button/buttonGoback";

// --- Models ---
import { createParameterUnitSchema, updateParameterUnitSchema } from "../../../models/schema/parameterUnit";
import { ParameterUnitItem } from "../../../models/types/parameterUnit";

// --- Utils ---
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoaderCircle } from "lucide-react";
import { UrlCollection } from "../../../common/url-collection";
import FormInputSpecialChar from "../../../components/form-input/FormInputSpecialChar";
import { useTranslation } from "react-i18next";

interface FormProps {
  isCreate: boolean;
  data: ParameterUnitItem | null;
}

export default function ParameterUnitCreateUpdate({ isCreate, data }: FormProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const schema = isCreate ? createParameterUnitSchema : updateParameterUnitSchema;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      code: '',
    },
  });

  useEffect(() => {
    if (data) {
      reset(omitMetaFields(data));
    }
  }, [data]);

  const onSubmit = async (values: any) => {
    setLoading(true);
    try {
      const res = isCreate ? await parameterUnitSvc.createParameterUnit(values) : await parameterUnitSvc.updateParameterUnit({ ...values, id: Number(data?.id) });
      if (res && res.content.status) {
        unlock();
        setLoading(false);
        notify(t("common.success"), isCreate ? t("parameterUnit.form.notifications.createSuccess") : t("parameterUnit.form.notifications.updateSuccess"), "success");
        navigate(UrlCollection.ParameterUnit);
      }
    } catch (e: any) {
      notify(t("common.error"), t("parameterUnit.form.notifications.error"), "danger");
    } finally {
      setLoading(false);
    }
  };

  const { showModal, confirmLeave, cancelLeave, confirmContent, unlock } = useUnsavedChangesWarning(isDirty);

  return (
    <>
      <ModalConfirm
        isOpen={showModal}
        onClose={cancelLeave}
        title={confirmContent.title}
        content={confirmContent.content}
        titleComfirm={t("parameterUnit.form.buttons.confirmLeave")}
        action={confirmLeave}
      />
      <div className="w-full h-full">
        <ButtonGoback />
        <div className="max-w-md mx-auto p-8 bg-white rounded-lg shadow mt-3">
          <h2 className="text-2xl font-bold mb-6 text-center">{isCreate ? t("parameterUnit.form.title.create") : t("parameterUnit.form.title.update")}</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-4 mb-4">
              <FormInputSpecialChar
                label={t("parameterUnit.form.labels.name")}
                name="name"
                register={register("name")}
                error={errors.name}
                required
                setValue={setValue}
                watch={watch}
              />
              <FormInputSpecialChar
                label={t("parameterUnit.form.labels.code")}
                name="code"
                register={register("code")}
                error={errors.code}
                required
                setValue={setValue}
                watch={watch}
              />
            </div>
            <button
              disabled={!isDirty}
              type="submit"
              className="btn_submit w-full mt-4"
            >
              <div className="flex items-center justify-center gap-2">
                {loading ? <LoaderCircle /> : ""}
                {isCreate ? t("parameterUnit.form.buttons.create") : t("parameterUnit.form.buttons.update")}
              </div>
            </button>
          </form>
        </div>
      </div>
    </>
  );
}